import {
  getTournamentDetailsLoading,
  getTournamentLastUpdated,
  getUserCreatedTournamentLoading,
  getUserTournamentLoading,
} from '../../../../corelogic/state/tournament/tournament.selector';

import { dependencies } from '../../../../configurations/dependencies/dependencies';
import { TOURNAMENT_DEFAULTS } from '../../../../corelogic/domain/constants/tournament.constant';
import {
  TournamentUpsert,
  UserTournamentUpsert,
} from '../../../../corelogic/domain/models/tournament.model';
import { isSameDay } from '../../../../corelogic/domain/utils/date.utils';
import { getAuthenticationToken } from '../../../../corelogic/state/authentication/authentication.selector';
import { store } from '../../../../corelogic/state/store';
import {
  retrieveGlobalTournamentDetails,
  retrieveTournamentDetails,
  retrieveTournaments,
  retrieveTournamentsByUser,
  retrieveTournamentsCreatedByUser,
} from '../../../../corelogic/state/tournament/tournament.action';
import { getTournamentLoading } from '../../../../corelogic/state/tournament/tournament.selector';

export const TournamentService = {
  isDataStale: (): boolean => {
    const lastUpdated = getTournamentLastUpdated(store.getState());
    const isLoading = getTournamentLoading(store.getState());

    if (isLoading) return false;

    // check if isLoading is false or if lastUpdated is more than 24 hours old
    return !lastUpdated || !isSameDay(lastUpdated, Date.now());
  },

  retrieveAllTournaments: () => {
    console.log('retrieveAllTournaments');
    const token = getAuthenticationToken(store.getState());
    if (!token) {
      console.warn('TournamentService: No authentication token found');
      return;
    }
    if (getTournamentLoading(store.getState())) {
      console.warn('TournamentService: Tournament is loading');
      return;
    }
    store.dispatch(retrieveTournaments({ token, retry: 0 }));
  },

  retrieveTournamentsByUser: () => {
    console.log('retrieveTournamentsByUser');
    const token = getAuthenticationToken(store.getState());
    if (!token) {
      console.warn('TournamentService: No authentication token found');
      return;
    }
    if (getUserTournamentLoading(store.getState())) {
      console.warn('TournamentService: User tournament is loading');
      return;
    }
    store.dispatch(retrieveTournamentsByUser({ token, retry: 0 }));
  },

  retrieveTournamentsCreatedByUser: () => {
    console.log('retrieveTournamentsCreatedByUser');
    const token = getAuthenticationToken(store.getState());
    if (!token) {
      console.warn('TournamentService: No authentication token found');
      return;
    }
    if (getUserCreatedTournamentLoading(store.getState())) {
      console.warn('TournamentService: User created tournament is loading');
      return;
    }
    store.dispatch(retrieveTournamentsCreatedByUser({ token, retry: 0 }));
  },

  retrieveTournamentDetails: (
    tournamentId: string,
    seasonIds: string[],
    isGlobal: boolean = false,
    year?: number,
    globalSeasonId?: string,
  ) => {
    const token = getAuthenticationToken(store.getState());
    if (!token) {
      console.warn('TournamentService: No authentication token found');
      return;
    }
    if (getTournamentDetailsLoading(store.getState())) {
      console.warn('TournamentService: Tournament details are loading');
      return;
    }

    if (isGlobal) {
      store.dispatch(
        retrieveGlobalTournamentDetails({
          token,
          limit: TOURNAMENT_DEFAULTS.MAX_GLOBAL_DISPLAYED_USERS,
          year,
          seasonId: globalSeasonId,
          retry: 0,
        }),
      );
    } else {
      store.dispatch(
        retrieveTournamentDetails({ token, tournamentId, seasonIds, retry: 0 }),
      );
    }
  },

  upsertTournament: async (
    tournament: TournamentUpsert,
    tournamentId?: string,
  ): Promise<TournamentUpsert | null> => {
    const token = getAuthenticationToken(store.getState());
    if (!token) {
      console.warn('TournamentService: No authentication token found');
      return null;
    }

    try {
      const result = await dependencies.tournamentsGateway.upsertTournament(
        token,
        tournament,
        tournamentId,
      );
      if (!result) {
        console.error('TournamentService: error upserting tournament');
        return null;
      }
      store.dispatch(retrieveTournaments({ token, retry: 0 }));
      store.dispatch(retrieveTournamentsByUser({ token, retry: 0 }));
      store.dispatch(retrieveTournamentsCreatedByUser({ token, retry: 0 }));

      // Extract tournament data from the response structure { tournament: { ... } }
      const resultWithTournament = result as any;
      if (resultWithTournament && resultWithTournament.tournament) {
        return resultWithTournament.tournament;
      }

      return result;
    } catch (error) {
      console.error('TournamentService: Error upserting tournament', error);
      return null;
    }
  },

  deleteTournament: async (tournamentId: string): Promise<boolean> => {
    const token = getAuthenticationToken(store.getState());
    if (!token) {
      console.warn('TournamentService: No authentication token found');
      return false;
    }

    try {
      await dependencies.tournamentsGateway.deleteTournament(
        token,
        tournamentId,
      );
      store.dispatch(retrieveTournaments({ token, retry: 0 }));
      store.dispatch(retrieveTournamentsByUser({ token, retry: 0 }));
      store.dispatch(retrieveTournamentsCreatedByUser({ token, retry: 0 }));
      return true;
    } catch (error) {
      console.error('TournamentService: Error deleting tournament', error);
      return false;
    }
  },

  upsertUserTournament: async (
    userTournament: UserTournamentUpsert,
  ): Promise<boolean> => {
    const token = getAuthenticationToken(store.getState());
    if (!token) {
      console.warn('TournamentService: No authentication token found');
      return false;
    }

    try {
      await dependencies.tournamentsGateway.upsertUserTournament(
        token,
        userTournament,
      );
      store.dispatch(retrieveTournamentsByUser({ token, retry: 0 }));
      return true;
    } catch (error) {
      console.error(
        'TournamentService: Error upserting user tournament',
        error,
      );
      return false;
    }
  },

  deleteUserTournament: async (tournamentId: string): Promise<boolean> => {
    const token = getAuthenticationToken(store.getState());
    if (!token) {
      console.warn('TournamentService: No authentication token found');
      return false;
    }

    try {
      await dependencies.tournamentsGateway.deleteUserTournament(
        token,
        tournamentId,
      );
      store.dispatch(retrieveTournamentsByUser({ token, retry: 0 }));
      store.dispatch(retrieveTournamentsCreatedByUser({ token, retry: 0 }));
      return true;
    } catch (error) {
      console.error('TournamentService: Error deleting user tournament', error);
      return false;
    }
  },

  validateTournamentPassword: async (
    tournamentId: string,
    password: string,
  ): Promise<boolean> => {
    const token = getAuthenticationToken(store.getState());
    if (!token) {
      console.warn('TournamentService: No authentication token found');
      return false;
    }

    try {
      const result =
        await dependencies.tournamentsGateway.validateTournamentPassword(
          tournamentId,
          password,
        );
      return result;
    } catch (error) {
      console.error(
        'TournamentService: Error validating tournament password',
        error,
      );
      return false;
    }
  },
};
